<template>
  <v-text-field v-model="currentText" @paste="onPaste"
                @keyup="$emit('keyup', $event)" @keypress="onKey"
                :error-messages="errorMessage"
                :inputmode="type === 'decimal'? 'decimal' : 'numeric'"
                v-bind="fieldProps"
  >
    <!-- Repassa ao v-text-field os slots que chegarem — o MultipleField usa o #append. -->
    <template v-for="(_, name) in $slots" #[name]="scope">
      <slot :name="name" v-bind="scope || {}"/>
    </template>
  </v-text-field>
</template>
<script lang="ts">
import {Component, Vue, Prop, Watch, toNative} from 'vue-facing-decorator'

@Component({emits: ['keyup', 'input']})
class NumberInput extends Vue {
  @Prop({type: Number}) value!: number | undefined
  @Prop({type: String}) label!: string
  @Prop({type: String}) type!: string
  @Prop({type: Boolean}) hideDetails!: boolean
  @Prop({type: String, default: () => ""}) errorMessage!: string

  currentText = ""

  /**
   * O que se repassa ao v-text-field. `value` e `type` ficam de fora de propósito: o
   * Vuetify 4 não os declara como props, então cairiam como atributos no <input>, onde
   * são mesclados depois do v-model — um `value: undefined` ali sobrescreve o texto
   * digitado a cada redesenho. `errorMessage` já segue adiante com o nome certo.
   */
  get fieldProps(): Record<string, unknown> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {value, type, errorMessage, ...rest} = this.$props as Record<string, unknown>
    return rest
  }

  mounted() {
    this.currentText = this.$utils.isNullOrUndefined(this.value) || isNaN(this.value!) ? "" : Number(this.value).toString()
  }

  @Watch('currentText')
  onCurrentTextChange(newText: string) {
    let value: number | undefined = newText ? Number(newText) : undefined
    if (value !== undefined && isNaN(value)) value = undefined
    else if (this.type === 'decimal') {
      value = (value !== undefined && !isNaN(value)) ? value : undefined
    } else value = (value !== undefined && Number.isInteger(value)) ? value : undefined
    this.$emit('input', value)
  }

  @Watch('value')
  onValueChange(newValue: number | undefined, oldValue: number | undefined) {
    if (newValue === oldValue) return
    const suggestedText = this.$utils.isNullOrUndefined(newValue) || isNaN(this.value!) ? "" : String(newValue)
    this.currentText = suggestedText
  }

  preview(text: string, start: number, end: number): string {
    const charArray = this.currentText.split('')
    charArray.splice(start, end - start, ...text.split(''))
    return charArray.join('')
  }

  onKey(event: KeyboardEvent) {
    return this.onType(event.key, event)
  }

  onPaste(event: ClipboardEvent) {
    return this.onType(event.clipboardData!.getData("Text"), event)
  }

  onType(typed: string, event: Event) {
    const target = event.target as HTMLInputElement
    const text = this.preview(typed, target.selectionStart!, target.selectionEnd!)
    const allowSign = true
    const allowDot = this.type === 'decimal'
    const regex = allowDot ? (allowSign ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/) : (allowSign ? /^-?\d*$/ : /^\d*$/)
    if (!regex.test(text)) {
      return event.preventDefault()
    }
  }
}

export default toNative(NumberInput)
</script>
